// ═══════════════════════════════════════════════════════════════
// ShipSwift API client
// Wraps fetch() with JWT auth, JSON encoding, and error normalization.
// All page components should use this rather than calling fetch() directly.
// ═══════════════════════════════════════════════════════════════

const TOKEN_KEY = 'shipswift_jwt';
const REFRESH_KEY = 'shipswift_refresh';

export const auth = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  loggedIn: () => !!localStorage.getItem(TOKEN_KEY),
  setRefresh: (refresh) => localStorage.setItem(REFRESH_KEY, refresh),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
};

export class APIError extends Error {
  constructor(status, body) {
    super(body?.detail || body?.message || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request(method, path, body, opts = {}) {
  const headers = {
    Accept: 'application/json',
    ...(opts.headers || {}),
  };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = auth.get();
  if (token && !opts.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body: body === undefined
      ? undefined
      : body instanceof FormData ? body : JSON.stringify(body),
  });

  // 204 No Content
  if (res.status === 204) return null;

  let payload = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    payload = await res.json().catch(() => null);
  } else if (contentType.includes('application/pdf') || contentType.includes('octet-stream')) {
    return await res.blob();
  } else {
    payload = await res.text().catch(() => null);
  }

  if (!res.ok) {
    if (res.status === 401) {
      auth.clear();
    }
    throw new APIError(res.status, payload);
  }
  return payload;
}

export const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body, opts) => request('POST', path, body, opts),
  put: (path, body, opts) => request('PUT', path, body, opts),
  patch: (path, body, opts) => request('PATCH', path, body, opts),
  delete: (path, opts) => request('DELETE', path, undefined, opts),
};

// ═══════════════════════════════════════════════════════════════
// Convenience wrappers for the most-used backend endpoints
// ═══════════════════════════════════════════════════════════════

export const Auth = {
  register: (data) => api.post('/auth/register', data, { skipAuth: true }),
  login: async (email, password) => {
    const res = await api.post('/auth/login', { email, password }, { skipAuth: true });
    if (res?.access_token) auth.set(res.access_token);
    return res;
  },
  logout: async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    auth.clear();
  },
  me: () => api.get('/merchants/me'),
};

export const Rates = {
  calculate: (data) => api.post('/tools/rate-calculator', data, { skipAuth: true }),
  serviceability: (pin) => api.get(`/tools/serviceability/${pin}`, { skipAuth: true }),
};

export const Orders = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/orders${qs ? '?' + qs : ''}`);
  },
  detail: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  book: (id, courierId, serviceCode) =>
    api.post(`/orders/${id}/book`, { courier_id: courierId, service_code: serviceCode }),
  cancel: (id, reason) => api.post(`/orders/${id}/cancel`, { reason }),
  label: (id) => api.get(`/orders/${id}/label`),
};

export const Wallet = {
  balance: () => api.get('/wallet/balance'),
  ledger: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/wallet/ledger${qs ? '?' + qs : ''}`);
  },
  rechargeInitiate: (amount_paise) => api.post('/wallet/recharge/initiate', { amount_paise }),
};

export const Acquisition = {
  segments: () => api.get('/acquisition/segments', { skipAuth: true }),
  checkUnserviceable: (data) => api.post('/acquisition/check-unserviceable', data, { skipAuth: true }),
  savings: (data) => api.post('/acquisition/savings-calculator', data, { skipAuth: true }),
  whatsappQuote: (data) => api.post('/acquisition/whatsapp-quick-quote', data, { skipAuth: true }),
  lead: (data) => api.post('/acquisition/lead', data, { skipAuth: true }),
};

export default api;

export const Merchants = {
  me: () => api.get('/merchants/me'),
  kycDocuments: () => api.get('/merchants/me/kyc-documents'),
  submitKycDocument: (docType, documentUrl, documentNumber = null) =>
    api.post('/merchants/me/kyc-documents', {
      document_type: docType,
      document_url: documentUrl,
      document_number: documentNumber || undefined,
    }),
};

export const Pickups = {
  list: () => api.get('/pickups/addresses'),
  create: (data) => api.post('/pickups/addresses', data),
  update: (id, data) => api.patch(`/pickups/addresses/${id}`, data),
  delete: (id) => api.delete(`/pickups/addresses/${id}`),
  setDefault: (id) => api.post(`/pickups/addresses/${id}/set-default`),
};

export const Uploads = {
  // Multipart — must stay as raw fetch, but uses auth.get() for token
  file: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/v1/uploads', {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.get()}` },
      body: fd,
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Upload failed');
    }
    return res.json();
  },
};

export const BulkOrders = {
  template: async () => {
    const res = await fetch('/api/v1/bulk-orders/bulk-template', {
      headers: { Authorization: `Bearer ${auth.get()}` },
    });
    if (!res.ok) throw new Error('Could not download template');
    return res.blob();
  },
  uploadCsv: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/v1/bulk-orders/bulk-csv', {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.get()}` },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Upload failed');
    return data;
  },
  bulkLabels: (orderIds) =>
    api.post('/bulk-orders/labels/bulk', { order_ids: orderIds }),
};

export const Tracking = {
  get: (awb) => api.get(`/track/${encodeURIComponent(awb)}`, { skipAuth: true }),
};

export const Volume = {
  slabs: () => api.get('/volume/slabs', { skipAuth: true }),
  mySlab: () => api.get('/volume/my-slab'),
  preview: (shipmentsPerMonth) =>
    api.get(`/volume/preview?shipments_per_month=${shipmentsPerMonth}`, { skipAuth: true }),
};
